/**
 * Display formatters for dates the firm reads, all fixed to the office timezone.
 *
 * The timezone is the reason these exist rather than each site calling `toLocaleString`. A bare
 * call formats in whatever zone the browser is in, so the same case read from outside Eastern
 * showed a different day than the one the API recorded, which runs on `America/New_York`.
 *
 * Each one guards its input rather than exposing `Intl.DateTimeFormat` directly, because
 * `Intl.format` throws `RangeError` on an Invalid Date. Several date fields arrive as an
 * unvalidated `z.string()`, and the app has a single error boundary on the root route, so one bad
 * value would blank the whole page where `toLocaleString` merely rendered "Invalid Date" in a cell.
 */
type DateFormatter = {
    format: (value: Date) => string;
};

const guard = (formatter: Intl.DateTimeFormat): DateFormatter => ({
    format: (value) => {
        if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
            console.error("Invalid date passed to a date formatter", value);
            return "Invalid Date";
        }

        return formatter.format(value);
    },
});

const timeZone = "America/New_York";

/** 09/28/2026 */
export const dateFormat = guard(
    new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }),
);

/** Sep 28, 2026 */
export const dateLongFormat = guard(
    new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "short",
        day: "numeric",
    }),
);

/** Sep 28, 2026, 2:30 PM */
export const dateTimeFormat = guard(
    new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
    }),
);

/** 09/28/2026, 02:30 PM */
export const dateTimeNumericFormat = guard(
    new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    }),
);
